import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getKbProfile, type AdminKbDetail } from '../lib/api';
import { theme } from '../theme';

export function KbDetailPage() {
  const { vehicleKey = '' } = useParams<{ vehicleKey: string }>();
  const [data, setData] = useState<AdminKbDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    getKbProfile(vehicleKey)
      .then(setData)
      .catch((err: Error) => setError(err.message));
  }, [vehicleKey]);

  if (error) return <p style={{ color: theme.status.error }}>Error: {error}</p>;
  if (!data) return <p>Loading…</p>;

  if (!data.profile) {
    return (
      <div>
        <Link to="/kb">← back to KB</Link>
        <h1>{data.vehicleKey}</h1>
        <p>No profile yet for this vehicle class.</p>
      </div>
    );
  }

  const { profile } = data;
  return (
    <div>
      <Link to="/kb">← back to KB</Link>
      <h1>{data.vehicleKey}</h1>
      <p style={{ color: theme.text.secondary, fontSize: 13 }}>
        {profile.sampleCount} sample scan(s) · last updated{' '}
        {new Date(profile.lastUpdatedAt).toLocaleString()}
      </p>

      {profile.signalSetStats && (
        <div
          style={{
            background: theme.surface.panel,
            border: `1px solid ${theme.surface.border}`,
            borderRadius: theme.radius.md,
            padding: '12px 16px',
            marginTop: 12,
            marginBottom: 24,
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: theme.text.tertiary, textTransform: 'uppercase' }}>
              OBDb avg readings/scan
            </div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {profile.signalSetStats.averageReadingCount}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: theme.text.tertiary, textTransform: 'uppercase' }}>
              Last scan
            </div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {profile.signalSetStats.lastReadingCount}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: theme.text.tertiary, textTransform: 'uppercase' }}>
              Total readings
            </div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {profile.signalSetStats.totalReadingsReturned.toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: theme.text.tertiary, textTransform: 'uppercase' }}>
              Across
            </div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {profile.signalSetStats.sampleCount} OBDb scans
            </div>
          </div>
        </div>
      )}

      <h2>Working commands ({profile.workingCommands.length})</h2>
      <p style={{ color: theme.text.secondary, fontSize: 12 }}>
        Ranked by Wilson score (confidence-adjusted success rate).
      </p>
      <table style={{ width: '100%' }}>
        <thead>
          <tr style={{ background: theme.surface.header }}>
            <Th>Header</Th>
            <Th>Filter</Th>
            <Th>Mode</Th>
            <Th>PID</Th>
            <Th>Successes</Th>
            <Th>Tries</Th>
            <Th>Confidence</Th>
            <Th>Last seen</Th>
          </tr>
        </thead>
        <tbody>
          {profile.workingCommands.map((cmd, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${theme.surface.border}` }}>
              <Td mono>{cmd.header}</Td>
              <Td mono>{cmd.receiveFilter ?? '—'}</Td>
              <Td mono>{cmd.mode}</Td>
              <Td mono>{cmd.pid}</Td>
              <Td>{cmd.successCount.toFixed(1)}</Td>
              <Td>{cmd.tries.toFixed(1)}</Td>
              <Td>{(cmd.wilsonScore * 100).toFixed(0)}%</Td>
              <Td>{new Date(cmd.lastSuccessAt).toLocaleDateString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>

      {profile.failingAddresses.length > 0 && (
        <>
          <h2>Failing addresses ({profile.failingAddresses.length})</h2>
          <p style={{ color: theme.text.secondary, fontSize: 12 }}>
            Addresses we've probed that never returned data — Layer 2/3 will skip these on next
            scan.
          </p>
          <table style={{ width: '100%' }}>
            <thead>
              <tr style={{ background: theme.surface.header }}>
                <Th>Header</Th>
                <Th>Tries</Th>
                <Th>Last attempt</Th>
              </tr>
            </thead>
            <tbody>
              {profile.failingAddresses.map((a, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${theme.surface.border}` }}>
                  <Td mono>{a.header}</Td>
                  <Td>{a.tries.toFixed(1)}</Td>
                  <Td>{new Date(a.lastAttemptedAt).toLocaleDateString()}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        color: theme.text.secondary,
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <td
      style={{
        padding: '8px 12px',
        fontSize: 13,
        fontFamily: mono ? theme.fonts.mono : 'inherit',
      }}
    >
      {children}
    </td>
  );
}
